import { useMutation, useQuery } from "@apollo/client";
import { StarIcon } from "@heroicons/react/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/react/solid";
import a from "indefinite";
import { compact } from "lodash";
import { useEffect, useRef } from "react";
import {
  ArtifactType,
  FeedbackState,
  GetArtifactSidebarQueryQuery,
  GetArtifactSidebarQueryQueryVariables,
} from "types/graphql-schema";

import ArtifactActivities from "@apps/artifact-sidebar/components/artifact-activities";
import SidebarLabel from "@apps/artifact-sidebar/components/sidebar-label";
import SidebarRow from "@apps/artifact-sidebar/components/sidebar-row";
import ArtifactDropdownMenu from "@apps/artifact/artifact-dropdown-menu";
import ArtifactActionItemCompleteToggle from "@apps/artifact/components/action-item-complete-toggle";
import ActionItemDueDate from "@apps/artifact/components/action-item-due-date";
import ActionItemAssignee from "@apps/artifact/components/artifact-assignee";
import DecisionStateComponent from "@apps/artifact/components/decision-state";
import getOverviewFavouriteGoalsQuery from "@apps/goal-overview/graphql/get-overview-favourite-goals-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ButtonSize } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import Sidebar from "@components/sidebar/sidebar";
import { maxKeyResults } from "@helpers/constants";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ArtifactSidebarCannotRead from "./components/cannot-read";
import DiscussionNotes from "./components/discussion-notes";
import ArtifactSidebarError from "./components/error";
import ArtifactSidebarFeedback from "./components/feedback";
import ArtifactSidebarHeader from "./components/header";
import ArtifactSidebarLoading from "./components/loading";
import ArtifactSidebarRecognition from "./components/recognition";
import ArtifactSidebarGoalSection from "./goal-section";
import addFavouriteArtifactMutation from "./graphql/add-favourite-artifact-mutation";
import getArtifactSidebarQuery from "./graphql/get-artifact-sidebar-query";
import removeFavouriteArtifactMutation from "./graphql/remove-favourite-artifact-mutation";

const ArtifactSidebarContent = ({
  artifactId,
  meetingId,
  meetingGroupId,
  onClose,
}: {
  artifactId: number;
  meetingId?: number;
  meetingGroupId?: number;
  onClose: (option: { redirect: boolean }) => void;
}) => {
  const currentOrganization = currentOrganizationVar();
  const actionItemStates = currentOrganization.actionItemStates
    ? compact(currentOrganization.actionItemStates)
    : [];
  const completeActionItemState = actionItemStates.find(
    (state) => state.isComplete,
  );
  const focusRef = useRef(null);
  const label = useLabel();
  const { loading, error, data, previousData } = useQuery<
    GetArtifactSidebarQueryQuery,
    GetArtifactSidebarQueryQueryVariables
  >(getArtifactSidebarQuery, {
    variables: {
      artifactId: artifactId,
      keyResultLimit: maxKeyResults,
    },
    errorPolicy: "ignore",
  });
  const artifact = data?.artifact;

  const handleCloseSidebar = () => {
    onClose({ redirect: false });
  };
  const handleDeleteArtifact = () => {
    onClose({ redirect: true });
  };

  useEffect(() => {
    // when the sidebar is displayed but then it is not accessible anymore
    // happens usually when deleting an artifact
    if (data?.artifact === null && previousData?.artifact?.id) {
      onClose({ redirect: true });
    }
  }, [data, previousData, onClose]);

  const currentUser = currentUserVar();
  const favouriteIds = assertEdgesNonNull(currentUser.favouriteArtifacts).map(
    ({ id }) => id,
  );
  const isFavourite = artifact && favouriteIds.includes(artifact.id);

  const [addFavouriteArtifact, { loading: loadingAdd }] = useMutation(
    addFavouriteArtifactMutation,
  );
  const [removeFavouriteArtifact, { loading: loadingRemove }] = useMutation(
    removeFavouriteArtifactMutation,
  );
  const loadingToggleFavourite = loadingRemove || loadingAdd;

  const handleClickFavouriteButton = () => {
    const variables = { artifactId: artifact?.id };
    if (isFavourite) {
      removeFavouriteArtifact({
        variables,

        refetchQueries: [getOverviewFavouriteGoalsQuery],
      });
    } else {
      addFavouriteArtifact({
        variables,

        refetchQueries: [getOverviewFavouriteGoalsQuery],
      });
    }
  };

  return (
    <Sidebar
      className="bg-gray-50"
      headerClassName="bg-white"
      onClose={handleCloseSidebar}
      additionalActions={
        artifact ? (
          <>
            {loadingToggleFavourite && <Loading mini size="4" />}
            <Button
              tooltip="Mark as favourite"
              theme={buttonTheme.iconGray}
              className="px-0 w-[28px]"
              aria-label="Artifact dropdown menu button"
              onClick={handleClickFavouriteButton}
            >
              {isFavourite ? (
                <SolidStarIcon className="h-5 w-5 text-yellow-500" />
              ) : (
                <StarIcon className="h-5 w-5" />
              )}
            </Button>
            <ArtifactDropdownMenu
              size={ButtonSize.large}
              artifact={artifact}
              portal={false} // because we are in a sidebar and already in portal
              onDelete={handleDeleteArtifact}
            />
          </>
        ) : undefined
      }
    >
      {!data && loading ? (
        <ArtifactSidebarLoading />
      ) : error ? (
        <ArtifactSidebarError />
      ) : artifact && !artifact.canRead.permission ? (
        <ArtifactSidebarCannotRead />
      ) : artifact ? (
        <div className="" aria-label="Sidebar artifact" ref={focusRef}>
          <div className="pt-1 px-4 sm:px-6 pb-3 bg-white flex justify-between gap-4">
            {artifact.__typename === "RecognitionArtifactNode" ? (
              <div className="flex-1 flex items-center gap-2 pt-2">
                <Avatar user={artifact.creator} size={6} />
                {artifact.creator ? (
                  <div className="text-normal font-medium text-gray-600">
                    <AppLink
                      to={getUrlUsingTypename(artifact.creator)}
                      className="hover:underline"
                    >
                      {artifact.creator.name}
                    </AppLink>{" "}
                    gave {a(label("recognition"))} to:
                  </div>
                ) : (
                  <div className="text-normal font-medium text-gray-600">
                    {a(label("recognition"))} was given to:
                  </div>
                )}
              </div>
            ) : (
              <ArtifactSidebarHeader artifact={artifact} />
            )}
          </div>

          {artifact.__typename === "ActionItemArtifactNode" && (
            <div className="px-4 sm:px-6 pb-6 bg-white flex gap-6 items-center text-gray-700">
              <div className="-ml-1 flex items-center">
                <ArtifactActionItemCompleteToggle
                  artifact={artifact}
                  className="px-1 py-1 hover:bg-gray-100 rounded"
                />
                <span className="text-sm text-gray-700">
                  {completeActionItemState?.label || "Done"}
                </span>
              </div>
              <div className="flex items-center">
                <ActionItemAssignee artifact={artifact} showName />
              </div>
              <div className="flex items-center group">
                <ActionItemDueDate artifact={artifact} />
              </div>
            </div>
          )}

          {artifact.__typename === "GoalArtifactNode" && (
            <ArtifactSidebarGoalSection artifact={artifact} />
          )}

          {artifact.__typename === "DecisionArtifactNode" && (
            <div className="pb-4 bg-white">
              <SidebarRow>
                <SidebarLabel>State</SidebarLabel>
                <DecisionStateComponent artifact={artifact} />
              </SidebarRow>
            </div>
          )}

          {artifact.artifactType === ArtifactType.Recognition && (
            <ArtifactSidebarRecognition artifact={artifact} />
          )}
          {artifact.artifactType === ArtifactType.Feedback && (
            <ArtifactSidebarFeedback artifact={artifact} />
          )}

          <div className="border-t border-b bg-white fs-mask">
            <DiscussionNotes
              key={artifact.id} // important to make sure discussion notes are refreshed when topic changes
              artifact={artifact}
            />
          </div>
          {(artifact.__typename !== "FeedbackArtifactNode" ||
            (artifact.__typename === "FeedbackArtifactNode" &&
              artifact.feedbackState === FeedbackState.Sent)) && (
            <div className="p-4 sm:p-6">
              <ArtifactActivities
                artifact={artifact}
                meetingId={meetingId}
                meetingGroupId={meetingGroupId}
              />
            </div>
          )}
        </div>
      ) : (
        <ArtifactSidebarError />
      )}
    </Sidebar>
  );
};

export default ArtifactSidebarContent;
